/**
 * API Client for Member Staging Operations
 */
import config from '../config';

// Use the same API base URL as the main client
const API_BASE_URL = config.API_URL;

class StagingAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authentication token from localStorage or context
   */
  getAuthToken() {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    const token = this.getAuthToken();
    
    console.log('🔍 Staging API Debug:', {
      endpoint,
      baseURL: this.baseURL,
      fullURL: `${this.baseURL}${endpoint}`,
      hasToken: !!token,
      tokenPrefix: token ? token.substring(0, 10) + '...' : 'No token'
    });
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',  
        'Expires': '0',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      
      console.log('📡 Staging API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorObj;
        try {
          errorObj = JSON.parse(errorText);
        } catch {
          errorObj = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error('❌ Staging API Error Response:', errorObj);
        throw new Error(errorObj.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Staging API Success:', result);
      return result;
      
    } catch (fetchError) {
      console.error('❌ Staging API Fetch Error:', {
        message: fetchError.message,
        name: fetchError.name,
        stack: fetchError.stack
      });
      
      // Re-throw with better error message for common issues
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please check if the server is running and try again.');
      }
      
      throw fetchError;
    }
  }

  /**
   * Add imported members to staging table
   */
  async stageMembersImport(groupId, members, importSource, importType) {
    try {
      console.log(`📤 Staging ${members.length} members for group ${groupId}`);
      console.log('📋 Request payload:', {
        groupId,
        membersCount: members.length,
        importSource,
        importType,
        sampleMember: members[0],
        baseURL: this.baseURL
      });
      
      const response = await this.request('/staging/import', {
        method: 'POST',
        body: JSON.stringify({
          groupId,
          members,
          importSource,
          importType
        })
      });

      console.log('📥 Staging API response:', response);
      console.log(`✅ Successfully staged ${response.staged_count} members`);
      return response;

    } catch (error) {
      console.error('❌ Error staging members:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get staging members for a group
   */
  async getStagingMembers(groupId, status = 'pending') {
    try {
      // Add cache busting parameter to prevent stale data
      const timestamp = Date.now();
      const response = await this.request(`/staging/${groupId}?status=${status}&_t=${timestamp}`);
      return response.staging_members;

    } catch (error) {
      console.error('❌ Error getting staging members:', error);
      throw error;
    }
  }

  /**
   * Get staging statistics for a group
   */
  async getStagingStats(groupId) {
    try {
      const response = await this.request(`/staging/${groupId}/stats`);
      return response.stats;

    } catch (error) {
      console.error('❌ Error getting staging stats:', error);
      throw error;
    }
  }

  // Native app will handle matching by querying staging table directly

  /**
   * Remove staging records
   */
  async removeStagingRecords(stagingIds, groupId) {
    try {
      const response = await this.request('/staging/records', {
        method: 'DELETE',
        body: JSON.stringify({ stagingIds, groupId })
      });

      return response;

    } catch (error) {
      console.error('❌ Error removing staging records:', error);
      throw error;
    }
  }

  /**
   * Clean up expired staging records
   */
  async cleanupExpired() {
    try {
      const response = await this.request('/staging/cleanup-expired', {
        method: 'POST'
      });

      return response;

    } catch (error) {
      console.error('❌ Error cleaning up expired records:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const stagingAPI = new StagingAPI();
export default stagingAPI;

// Export individual methods for convenience
export const {
  stageMembersImport,
  getStagingMembers,
  getStagingStats,
  removeStagingRecords,
  cleanupExpired
} = stagingAPI;